import { ChipOwnProps } from "@mui/material";
import ModeStandbyIcon from "@mui/icons-material/ModeStandby";
import AttributionRoundedIcon from "@mui/icons-material/AttributionRounded";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

type HA_ROLE_BASE = {
  icon: JSX.Element;
  color: ChipOwnProps["color"];
  title: string;
};

type HA_ROLE_SCHEMA = {
  master: HA_ROLE_BASE;
  standby: HA_ROLE_BASE;
  standalone: HA_ROLE_BASE;
};

const conf = {
  ROLES: ["support", "approver", "admin"],
  HA_ROLE: {
    master: {
      title: "PRIMARY",
      color: "primary",
      icon: <CheckCircleOutlineIcon />,
    },
    standby: {
      title: "STANDBY",
      color: "secondary",
      icon: <ModeStandbyIcon />,
    },
    standalone: {
      title: "STANDALONE",
      color: "secondary",
      icon: <AttributionRoundedIcon />,
    },
  } as HA_ROLE_SCHEMA,
  COLUMNS: {
    CPU: ["Usage (%)", "Temperature (˚C)"],
    HSM: [
      "Serial No.",
      "Vendor",
      "Model",
      "Temperature (˚C)",
      "Adapter clock",
      "FW Version",
      "Raw",
    ],
    BASE: ["Hardware", "OS", "Timezone", "Kernel", "Adapter clock", "Raw"],
    HOST: [
      "Name",
      "Version",
      "Fan mode",
      "Power supply 1",
      "Power supply 2",
      "Raw",
    ],
     LOGICAL_DISK: [
      "Filesystem",
      "Size",
      "Used",
      "Available",
      "Usage (%)",
      "Mounted on",
      "Raw",
    ],
    PHYSICAL_DISK: [
      "Model",
      "Serial No.",
      "FW Version",
      "Size",
      "Power-On hours",
      "Power-Cycle count",
      "Raw",
    ],
    LOGS: ["Name", "Status", "Last updated", "Raw"],
    USERS: [
      { key: "email", label: "User", isHeader: true },
      {
        key: "status",
        label: "Account",
        description: "Denotes current account status",
      },
      { key: "firstName", label: "First name" },
      { key: "lastName", label: "Last name" },
      { key: "project.name", label: "Active project" },
      { key: "project.updated", label: "Time elapsed" },
    ],
    AUDIT: [
      { key: "email", label: "User", isHeader: true },
      { key: "updatedAt", label: "Date" },
      { key: "status", label: "Status" },
      { key: "type", label: "Event" },
      { key: "project", label: "Project" },
      { key: "host", label: "Host" },
    ],
    MEMORY: ["Available", "Free", "Total"],
    HISTORY: [
      { key: "updatedAt", label: "Timestamp", isHeader: true },
      { key: null, label: "CPU:" },
      { key: null, label: "Usage (%)" },
      { key: null, label: "Temperature (˚C)" },
      { key: null, label: "Memory (MB):" },
      { key: null, label: "Available" },
      { key: null, label: "Free" },
      { key: null, label: "Total" },
      { key: null, label: "Role" },
      { key: null, label: "Adapter clock" },
      { key: null, label: "Raw" },
    ],
    PROJECTS: {
      ADMIN: [
        { key: "name", label: "Name", isHeader: true },
        { key: "name", label: "Tags" },
        { key: "version", label: "Version" },
        { key: "user.lastActive", label: "Last active" },
        { key: "user.email", label: "Active user" },
        { key: "user.lastActive", label: "Time elapsed" },
        { key: "online", label: "Allow access" },
      ],
      MANAGE: [
        { key: "name", label: "Name", isHeader: true },
        { key: "name", label: "Tags" },
        { key: "user.lastActive", label: "Last active" },
        { key: "user.email", label: "Active user" },
        { key: "project.assigned", label: "Assigned" },
      ],
      SUPPORT: [
        { key: "name", label: "Name", isHeader: true },
        { key: "name", label: "Tags" },
        { key: "version", label: "Version" },
        { key: "user.lastActive", label: "Last active" },
        { key: "user.email", label: "Active user" },
        { key: "user.lastActive", label: "Time elapsed" },
        { key: "online", label: "Status" },
      ],
    },
    SERVICES: [
      "Name",
      "Status",
      "Tasks",
      "Memory",
      "CPU",
      "PID",
      "Last updated",
      "Raw",
    ],
    AUDIT_CSV: [
      "id",
      "user",
      "route",
      "date",
      "type",
      "status",
      "host",
      "project",
      "note",
    ],
    REQUESTS: {
      PENDING: [
        { key: "status", label: "Status", isHeader: true },
        { key: "requester", label: "Requester" },
        { key: "subject", label: "Subject" },
        { key: "project", label: "Project" },
        { key: "updatedAt", label: "Issued" },
      ],
      CLOSED: [
        { key: "status", label: "Status", isHeader: true },
        { key: "requester", label: "Requester" },
        { key: "approver", label: "Approver" },
        { key: "subject", label: "Subject" },
        { key: "project", label: "Project" },
        { key: "updatedAt", label: "Issued" },
      ],
    },
    APPLIANCES: [
      { key: "host", label: "Host", isHeader: true },
      { key: null, label: "Status" },
      { key: "ip", label: "IP" },
      { key: "serialNumber", label: "Serial No." },
      {
        key: "version",
        label: "Release",
        description: "Denotes active GRACE rpm version",
      },
      { key: null, label: "Role" },
    ],
  },
  HEIGHT: { NAVBAR: "63px", FOOTER: "43px" },
  STYLE: {
    JSON: {
      p: 2,
      m: 4,
      boxShadow: 2,
      color: "text.primary",
      backgroundColor: "background.default",
    },
    DIALOG: {
      p: 2,
      boxShadow: 2,
      backgroundColor: "background.default",
    },
    HISTORY: {
      m: 1,
      p: 2,
      boxShadow: 2,
      backgroundColor: "background.default",
    },
  },
  LAST_CHILD_NO_BORDER: { "&:last-child td, &:last-child th": { border: 0 } },
};

export default conf;
